import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import LoginPage from "./pages/LoginPage";
import StaffDashboard from "./pages/staff/StaffDashboard";
import StaffActivities from "./pages/staff/StaffActivities";
import StaffTimesheet from "./pages/staff/StaffTimesheet";
import StaffLeave from "./pages/staff/StaffLeave";
import StaffTodos from "./pages/staff/StaffTodos";
import StaffSignups from "./pages/staff/StaffSignups";
import StaffCommissions from "./pages/staff/StaffCommissions";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminStaff from "./pages/admin/AdminStaff";
import AdminPerformance from "./pages/admin/AdminPerformance";
import AdminLeaves from "./pages/admin/AdminLeaves";
import AdminTimesheets from "./pages/admin/AdminTimesheets";
import AdminAudit from "./pages/admin/AdminAudit";
import AdminTodos from "./pages/admin/AdminTodos";
import AdminSignups from "./pages/admin/AdminSignups";
import AdminCommissions from "./pages/admin/AdminCommissions";
import Layout from "./components/Layout";
import "./App.css";

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/portal"} replace />;
  }
  
  return <Layout>{children}</Layout>;
};

const AppRoutes = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }
  
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={user.role === "admin" ? "/admin" : "/portal"} /> : <LoginPage />} />
      
      {/* Staff Routes */}
      <Route path="/portal" element={<ProtectedRoute allowedRoles={["staff", "admin"]}><StaffDashboard /></ProtectedRoute>} />
      <Route path="/portal/activities" element={<ProtectedRoute allowedRoles={["staff", "admin"]}><StaffActivities /></ProtectedRoute>} />
      <Route path="/portal/timesheet" element={<ProtectedRoute allowedRoles={["staff", "admin"]}><StaffTimesheet /></ProtectedRoute>} />
      <Route path="/portal/leave" element={<ProtectedRoute allowedRoles={["staff", "admin"]}><StaffLeave /></ProtectedRoute>} />
      <Route path="/portal/todos" element={<ProtectedRoute allowedRoles={["staff", "admin"]}><StaffTodos /></ProtectedRoute>} />
      <Route path="/portal/signups" element={<ProtectedRoute allowedRoles={["staff", "admin"]}><StaffSignups /></ProtectedRoute>} />
      <Route path="/portal/commissions" element={<ProtectedRoute allowedRoles={["staff", "admin"]}><StaffCommissions /></ProtectedRoute>} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute allowedRoles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/staff" element={<ProtectedRoute allowedRoles={["admin"]}><AdminStaff /></ProtectedRoute>} />
      <Route path="/admin/performance" element={<ProtectedRoute allowedRoles={["admin"]}><AdminPerformance /></ProtectedRoute>} />
      <Route path="/admin/leaves" element={<ProtectedRoute allowedRoles={["admin"]}><AdminLeaves /></ProtectedRoute>} />
      <Route path="/admin/timesheets" element={<ProtectedRoute allowedRoles={["admin"]}><AdminTimesheets /></ProtectedRoute>} />
      <Route path="/admin/audit" element={<ProtectedRoute allowedRoles={["admin"]}><AdminAudit /></ProtectedRoute>} />
      <Route path="/admin/todos" element={<ProtectedRoute allowedRoles={["admin"]}><AdminTodos /></ProtectedRoute>} />
      <Route path="/admin/signups" element={<ProtectedRoute allowedRoles={["admin"]}><AdminSignups /></ProtectedRoute>} />
      <Route path="/admin/commissions" element={<ProtectedRoute allowedRoles={["admin"]}><AdminCommissions /></ProtectedRoute>} />
      
      {/* Default redirect */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors closeButton />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
